import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DataNodeModel, NodeKind } from './diagram/nodes/DataNodeModel';
import { IMetadatas, RuntimeMode, ITopology } from '../../app/apiSlice';
import { SelectedLabels } from '@kosmos/shared';
import { stateMessage } from '../libraries/cdsApiSlice';
import { DebugSource } from '../../components/StepByStep/StepByStep';

export interface FormParamInfo {
  priorities: string[];
  affinities: string[];
}

export interface CatalogLabels {
  values: CatalogLabel[];
}
export interface CatalogLabel {
  id: string;
  i18n: string | Record<string, any>;
  // on utilise pas les autres champs à ce jour
}

export interface NodeDefinitions {
  sources: NodeDefinition[];
  functions: NodeDefinition[];
  sinks: NodeDefinition[];
  labels?: CatalogLabels;
}

export interface RawDefinition {
  image: string;
  name: string;
}

export interface formAvroError {
  nodeId:string;
  error:string;
}

export interface NodeDefinition {
  name: string;
  type: string;
  kind: NodeKind;
  brut?: boolean;
  width?: number,
  height?: number,
  code?: string,
  icon?: string;
  description: string;
  raw?: RawDefinition;
  tabs?: TabDefinition[];
  settings: { [key: string]: FieldDefinition };
  containers?:
    | { name: string; image: string; [key: string]: string }[]
    | undefined;
  input?: PortDefinition[] | undefined;
  output?: PortDefinition[] | undefined;
  sideout?: SideOutPortDefinition[] | undefined;
  runtimeMode?: RuntimeMode;
  metadatas?: SelectedLabels;
  translatedMetadatas?: SelectedLabels;
  author?: string;
  noDefaultProperties?: boolean;
  isDeprecated?: boolean;
}

export interface PortDefinition {
  id: string;
  name: string;
  type: string;
  no_render_condition: Condition
  condition?:Condition
  fields: string[];
  actions:Action[]
}

export interface Action {
  action: string
  params?:{ [key: string]: any };
  sideout?:{ [key: string]: any };
  condition?:Condition
}

export interface SideOutPortDefinition {
  id: string;
  name: string;
  schema: string;

}

export interface FieldDefinition {
  name: string;
  type: string;
  inference?: boolean;
  type_param?: string;
  is_advanced?: boolean;
  required?: boolean;
  no_render?: boolean;
  hidden?: boolean;
  disabled?: boolean;
  default_value?: any;
  placeholder?: string;
  help?: string;
  value?: any;
  runtimeMode?: string;
  condition?: Condition;
  dependencies?: IDependencies[];
  no_indent?: boolean;
  regex?: string;
  regex_msg?: string;
}

export interface Condition {
  keyName: string;
  values: any[];
}

export interface IDependencies {
  keyName: string;
  values: IDependenciesValue[];
}

export interface IDependenciesValue {
  value: unknown;
  replace_value: string;
}

export interface TabDefinition {
  name: string;
  fields: string[];
  description?: string;
  advanced_description?: string;
}

export interface ConsoleState {
  pane: 'console' | 'realtime' | undefined;
  arg: any;
}

export interface SidebarState {
  pane: 'debug' | 'settings' | undefined;
  arg: any;
}

export interface IStreamNameAndRunID {
  name: string;
  runID: string;
  topologyId: string;
}

export enum PipelineState {
  STARTING,
  RUNNING,
  COMPLETED,
  STOPPING,
  STOPPED,
  DELETING,
  DELETED,
  FAILED,
  UNDEFINED,
}

export interface EditorState {
  // current topology
  current?: ITopology;
  topologyData: any[];
  changedTopology: boolean;
  restoredTopology?: string;
  streamNameAndRunID?: IStreamNameAndRunID;
  currendNodesDefinitions: NodeDefinitions | undefined;
  currentNode: DataNodeModel | undefined;
  currentNodeQuery: NodeDefinition[];
  console: ConsoleState;
  sidebar: SidebarState;
  formAvroError:formAvroError[]
  zoom: number;
  filter: string;
  sizePanel:string;
  debugMode: boolean;
  debugSource: DebugSource;
  pipelineState: PipelineState;
}

const initialState: EditorState = {
  current: undefined,
  topologyData: [],
  formAvroError:[],
  changedTopology: false,
  streamNameAndRunID: undefined,
  currendNodesDefinitions: undefined,
  currentNode: undefined,
  currentNodeQuery: [],
  console: {
    pane: 'console',
    arg: undefined,
  },
  sidebar: {
    pane: 'settings',
    arg: undefined,
  },
  zoom: 100,
  sizePanel:"70%",
  filter: '',
  debugMode: false,
  debugSource: DebugSource.FLOW,
  pipelineState: PipelineState.UNDEFINED,
};

export type SettingsValues = { [key: string]: any, valueWidget?:any };


export const editorSlice = createSlice({
  name: 'editor',
  initialState: initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    resetEditorSlice: () => initialState,
    setCurrentNodeDefinitions: (
      state,
      action: PayloadAction<NodeDefinitions>
    ) => {
      state.currendNodesDefinitions = action.payload;
    },
    setPipelineState: (state, action: PayloadAction<PipelineState>) => {
      state.pipelineState = action.payload;
    },
    setCurrentNode: (state, action: PayloadAction<DataNodeModel>) => {
      state.currentNode = action.payload;
    },
    setStreamNameAndRunID: (state, action: PayloadAction<{ name: string, runID: string, topologyId: string}>) => {
      state.streamNameAndRunID = action.payload;
    },
    seTErrorFormAvro: (state, action: PayloadAction<formAvroError[]>) => {
      state.formAvroError = action.payload;
    },
    addErrorFormAvro: (state, action: PayloadAction<formAvroError>) => {
      state.formAvroError.push(action.payload)
    },


    currentNodeQuery: (state, action: PayloadAction<NodeDefinition[]>) => {
      state.currentNodeQuery = action.payload;
    },

    setZoom: (state, action: PayloadAction<number>) => {
      state.zoom = action.payload;
    },

    setEnvironnement: (state, action: PayloadAction<string>) => {
      if (state.current) {
        state.current.environnement = action.payload;
        state.changedTopology = true;
      } else {
        console.log('Failed to update topology environnement !');
      }
    },

    setLabels: (state, action: PayloadAction<IMetadatas>) => {
      if (state.current) {
        state.current.metadatas = action.payload;
        state.changedTopology = true;
      } else {
        console.log('Failed to update Labels !');
      }
    },

    setForcedflowname: (state, action: PayloadAction<string>) => {
      if (state.current) {
        state.current.forcedflowname = action.payload;
        state.changedTopology = true;
      } else {
        console.log('Failed to update topology forcedFlowName !');
      }
    },

    setFilter: (state, action: PayloadAction<string>) => {
      state.filter = action.payload;
    },

    UpdateZoom: (state, action: PayloadAction<number>) => {
      state.zoom = state.zoom + action.payload;
    },

    // Use the PayloadAction type to declare the contents of `action.payload`
    // updateNodesTree: (state, action: PayloadAction<DataCatalog>) => {
    // @ts-ignore
    // state.catalog = action.payload;
    // },
    changeConsolePane: (
      state,
      action: PayloadAction<
        { pane: 'console' | 'realtime'; arg: any } | undefined
      >
    ) => {
      // @ts-ignore
      state.console = action.payload;
    },
    changeSidebarPane: (
      state,
      action: PayloadAction<
        | {
        pane:
          | 'configuration'
          | 'multiSelection'
          | 'settings'
          | 'runtimes'
          | 'documentation'
          | 'versions'
          | 'cron'
          | 'flows';
        arg?: any;
      }
        | undefined
      >
    ) => {
      // @ts-ignore
      state.sidebar = action.payload;
      state.sizePanel = "70%"
    },

    setTopology: (state, action: PayloadAction<ITopology>) => {
      state.current = action.payload;
    },

    setTopologyData: (state, action: PayloadAction<string>) => {
      try {
        state.topologyData = JSON.parse(action.payload);
      } catch (error) {
        console.error('Error while parsing topology data !');
      }
    },

    setTopologyName: (state, action: PayloadAction<string>) => {
      if (state.current) {
        state.current.name = action.payload;
        state.changedTopology = true;
      } else {
        console.log('Failed to update topology name !');
      }
    },
    setTopologyDescription: (state, action: PayloadAction<string>) => {
      if (state.current) {
        state.current.description = action.payload;
        state.changedTopology = true;
      } else {
        console.log('Failed to update topology description !');
      }
    },
    setTopologyParallelism: (state, action: PayloadAction<number>) => {
      if (state.current) {
        state.current.parallelism = action.payload;
        state.changedTopology = true;
      } else {
        console.log('Failed to update topology parallelism !');
      }
    },
    setTopologyModeBatch: (state, action: PayloadAction<boolean>) => {
      if (state.current) {
        state.current.modeBatch = action.payload;
        state.changedTopology = true;
      } else {
        console.log('Failed to update topology mode batch !');
      }
    },
    setTopologyRetentionTime: (state, action: PayloadAction<string>) => {
      if (state.current) {
        state.current.retentionTimeNats = action.payload;
        state.changedTopology = true;
      } else {
        console.log('Failed to update topology time retention !');
      }
    },
    setTopologyMessageStore: (state, action: PayloadAction<boolean>) => {
      if (state.current) {
        state.current.messageStore = action.payload;
        state.changedTopology = true;
      } else {
        console.log('Failed to update topology message store !');
      }
    },
    setTopologyGlobalConfigAdditions: (state, action: PayloadAction<string>) => {
      if (state.current) {
        state.current.globalConfigAdditions = action.payload;
        state.changedTopology = true;
      } else {
        console.log('Failed to update topology globalConfigAdditions !');
      }
    },
    setTopologyMemoryRequest: (state, action: PayloadAction<string>) => {
      if (state.current) {
        state.current.memoryRequest = action.payload;
        state.changedTopology = true;
      } else {
        console.log('Failed to update topology memory request !');
      }
    },
    setTopologyMemoryLimit: (state, action: PayloadAction<string>) => {
      if (state.current) {
        state.current.memoryLimit = action.payload;
        state.changedTopology = true;
      } else {
        console.log('Failed to update topology memory limit !');
      }
    },
    setTopologyCPURequest: (state, action: PayloadAction<string>) => {
      if (state.current) {
        state.current.cpuRequest = action.payload;
        state.changedTopology = true;
      } else {
        console.log('Failed to update topology CPU Request !');
      }
    },
    setTopologyCPULimit: (state, action: PayloadAction<string>) => {
      if (state.current) {
        state.current.cpuLimit = action.payload;
        state.changedTopology = true;
      } else {
        console.log('Failed to update topology CPU limit !');
      }
    },
    setTopologyRuntimeMode: (state, action: PayloadAction<string>) => {
      if (state.current) {
        state.current.runtimeMode = action.payload;
        state.changedTopology = true;
      } else {
        console.log('Failed to update topology runtime mode !');
      }
    },
    setTopologyRuntimeVersion: (state, action: PayloadAction<string>) => {
      if (state.current) {
        state.current.runtimeVersion = action.payload;
        state.changedTopology = true;
      } else {
        console.log('Failed to update topology runtime version !');
      }
    },
    setSizePanel: (state, action: PayloadAction<string>) => {
      if (state.current) {
        state.sizePanel = action.payload;
      } else {
        console.log('Failed to update topology priority !');
      }
    },
    updateCronConfig: (state, action: PayloadAction<{ schedule: string  }>) => {
      if (state.current) {
        state.current.schedule = action.payload.schedule;
        state.changedTopology = true;
      } else {
        console.error('Failed to update crontab property');
      }
    },
    setChangedTopology: (state, action: PayloadAction<boolean>) => {
      state.changedTopology = action.payload;
    },
    setRestoredTopology: (state, action: PayloadAction<string>) => {
      state.restoredTopology = action.payload;
    },
    setDebugMode: (state, action: PayloadAction<boolean>) => {
      state.debugMode = action.payload;
    },
    setDebugSource: (state, action: PayloadAction<DebugSource>) => {
      state.debugSource = action.payload;
    },
  },
});

export const {
  changeConsolePane,
  changeSidebarPane,
  setCurrentNode,
  setPipelineState,
  setZoom,
  setFilter,
  currentNodeQuery,
  setCurrentNodeDefinitions,
  setStreamNameAndRunID,
  resetEditorSlice,
  setTopology,
  setTopologyData,
  setChangedTopology,
  setRestoredTopology,
  setTopologyName,
  setTopologyDescription,
  setTopologyParallelism,
  setTopologyMemoryRequest,
  setTopologyMemoryLimit,
  setTopologyCPURequest,
  setTopologyCPULimit,
  setTopologyRuntimeVersion,
  setTopologyRetentionTime,
  setLabels,
  setEnvironnement,
  setSizePanel,
  updateCronConfig,
  setForcedflowname,
  setTopologyModeBatch,
  setTopologyMessageStore,
  setTopologyGlobalConfigAdditions,
  setDebugMode,
  setDebugSource,
} = editorSlice.actions;

// // The function below is called a selector and allows us to select a value from
// // the state. Selectors can also be defined inline where they're used instead of
// // in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
// export const selectCount = (state: RootState) => state.profile.value;
//
// // We can also write thunks by hand, which may contain both sync and async logic.
// // Here's an example of conditionally dispatching actions based on current state.
// export const incrementIfOdd = (amount: number): AppThunk => (
//     dispatch,
//     getState
// ) => {
//     const currentValue = selectCount(getState());
//     if (currentValue % 2 === 1) {
//         dispatch(incrementByAmount(amount));
//     }
// };

export default editorSlice.reducer;
